import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { Table, createColumnHelper } from '@bestdoctor/spectrum-uikit/Table';
import type { ColumnDef } from '@tanstack/react-table';
import { Text } from '@bestdoctor/spectrum-uikit/Text';

import { Loader } from 'shared/loaders/Loader';
import {
  TableHistoryPropsActions,
  HistoryAction,
  ICompanyActionTable,
} from 'interfaces/patients/patientInterfaces';

import { getRowChangeString, throttle } from 'utils/tablesMethods';
import { lineBreaker } from 'utils/lineBreaker';

const columnHelper = createColumnHelper<ICompanyActionTable>();

function createData(
  company: string,
  date: string,
  date_action: string,
  user: string,
  action_category: string,
  old_value: string,
  new_value: string,
  full_name: string,
  take_effect_at: string,
  verbose_changed_field_name: string,
  uuid: string
): ICompanyActionTable {
  return {
    company,
    date,
    date_action,
    user,
    action_category,
    old_value,
    new_value,
    full_name,
    take_effect_at,
    verbose_changed_field_name,
    uuid,
  };
}

const TableHistoryActions: React.FC<TableHistoryPropsActions> = ({
  historyActions,

  isLoadingCompanyActions,
  hasNextPageCompanyActions,
  isFetchingNextPageCompanyActions,

  orderingTableHistory,
  handleOrderingTableHistory,

  fetchNextPageCompanyActions,
}) => {
  const rows = useMemo(
    () =>
      historyActions.map((item: HistoryAction) =>
        createData(
          item.company,
          item.date,
          item.date_action,
          item.user,
          item.action_category,
          item.old_value,
          item.new_value,
          item.full_name,
          item.take_effect_at,
          item.verbose_changed_field_name,
          item.uuid
        )
      ),
    [historyActions]
  );

  // Сортировка
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  useEffect(() => {
    if (orderingTableHistory) {
      const isDesc = orderingTableHistory.startsWith('-');
      const fieldId = isDesc ? orderingTableHistory.slice(1) : orderingTableHistory;
      setSorting([{ id: fieldId, desc: isDesc }]);
    } else {
      setSorting([]);
    }
  }, [orderingTableHistory]);

  const handleSortingChange = useMemo(
    () => (newSorting: { id: string; desc: boolean }[]) => {
      if (newSorting.length > 0) {
        const sort = newSorting[0];
        handleOrderingTableHistory(sort.desc ? `-${sort.id}` : sort.id);
      } else {
        handleOrderingTableHistory('');
      }
    },
    [handleOrderingTableHistory]
  );

  const tableSorting = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return {
      state: sorting,
      setState: (updater: any) => {
        const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
        handleSortingChange(newSorting);
      },
    };
  }, [sorting, handleSortingChange]);

  // Бесконечная прокрутка
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(
    async (): Promise<void> => {
      if (!tableContainerRef.current) return;

      const { scrollHeight, scrollTop, clientHeight } = tableContainerRef.current;
      const bottom = scrollHeight - scrollTop <= clientHeight + 10;

      if (bottom && hasNextPageCompanyActions && !isFetchingNextPageCompanyActions) {
        await fetchNextPageCompanyActions();
      }
    },
    [hasNextPageCompanyActions, isFetchingNextPageCompanyActions, fetchNextPageCompanyActions]
  );

  const throttledHandleScroll = useMemo(() => throttle(handleScroll, 1000), [handleScroll]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container && hasNextPageCompanyActions && !isFetchingNextPageCompanyActions) {
      container.addEventListener('scroll', throttledHandleScroll);
      return () => {
        container.removeEventListener('scroll', throttledHandleScroll);
      };
    }
    return undefined;
  }, [hasNextPageCompanyActions, isFetchingNextPageCompanyActions, throttledHandleScroll]);

  // Колонки
  const columns = useMemo<ColumnDef<ICompanyActionTable, any>[]>(
    () => [
      columnHelper.accessor('date', {
        id: 'date',
        header: () => <Text as="span" variant="body-m">Дата изменения</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('user', {
        id: 'user__email',
        header: () => <Text as="span" variant="body-m">Изменения внёс</Text>,
        cell: (info) => <Text as="span" variant="body-m">{lineBreaker(info.getValue())}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor(
        (row) => getRowChangeString(row.action_category, row.verbose_changed_field_name),
        {
          id: 'category',
          header: () => <Text as="span" variant="body-m">Действие</Text>,
          cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }
      ) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('full_name', {
        id: 'slot__patient__last_name',
        header: () => <Text as="span" variant="body-m">ФИО</Text>,
        cell: (info) => <Text as="span" variant="body-m">{lineBreaker(info.getValue())}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('date_action', {
        id: 'date_action',
        header: () => <Text as="span" variant="body-m">Дата действия</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('old_value', {
        id: 'old_value',
        header: () => <Text as="span" variant="body-m">Старые данные</Text>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Text as="span" variant="body-m">
              {row.action_category !== 'D' ? lineBreaker(info.getValue()) : '-'}
            </Text>
          );
        },
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('new_value', {
        id: 'new_value',
        header: () => <Text as="span" variant="body-m">Новые данные</Text>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Text as="span" variant="body-m">
              {row.action_category !== 'D' ? lineBreaker(info.getValue()) : '-'}
            </Text>
          );
        },
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
    ],
    []
  );

  return (
    <Box
      ref={tableContainerRef}
      sx={{
        width: '100%',
        maxHeight: '256px',
        overflowY: 'auto',
      }}
    >
      <Table data={rows} columns={columns} sorting={tableSorting} enableSorting />
      {(isFetchingNextPageCompanyActions || isLoadingCompanyActions) && <Loader />}
    </Box>
  );
};

export { TableHistoryActions };
